import { useCallback } from 'react';
import { FloatingPortal, useClick, useDismiss, useFloating, useInteractions, useRole } from '@floating-ui/react';
import { css, cx } from '@linaria/atomic';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button } from '../../button/button.component';
import { ImagePreviewModalViewProps } from '../provider/image-preview-modal-controls';
import { MIN_ZOOM, useImagePreviewActions } from '../provider/image-preview.provider';
import { controlStyles } from './constants';

const modalBaseStyle = css`
  position: fixed;
  left: 0;
  width: 100dvw;
  background: #00215280;
  display: grid;
  grid-template-rows: 44px auto 30px;
  gap: ${theme.spacing(4)};
  z-index: 1000;
  backdrop-filter: blur(${theme.spacing(0.5)});
  padding: ${theme.spacing(0, 5, 6, 5)};

  @media (max-width: ${breakpoints.small.min}px) {
    padding: ${theme.spacing(0, 1.5, 6, 1.5)};
  }
`;

const headerStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing(1.5)};
  padding-bottom: 0;
`;

const closeControlStyle = css`
  padding: 3px;
`;

const closeButtonStyle = css`
  height: 24px;
  width: 24px;
  outline: none;
`;

export interface ImagePreviewModalProps extends ImagePreviewModalViewProps {
  heading?: React.ReactNode;
  children: React.ReactNode;
}

export const ImagePreviewModal = ({ onClose, show, heading, children }: ImagePreviewModalProps) => {
  const { changeZoom, setRotationDegree } = useImagePreviewActions();
  const zoomOutAndClose = useCallback(() => {
    setRotationDegree(0);
    changeZoom(MIN_ZOOM);
    onClose();
  }, [onClose]);

  const { refs, context } = useFloating({
    open: show,
    onOpenChange: (open) => {
      if (!open) zoomOutAndClose();
    },
  });

  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'dialog' });
  const click = useClick(context);

  const { getFloatingProps } = useInteractions([dismiss, role, click]);

  if (!show) return <></>;
  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        className={modalBaseStyle}
        style={{
          // @ts-ignore heightOffset is dynamically available on theme at runtime
          '--heightOffset': `${theme.heightOffset}px`,
          height: 'calc(100dvh - var(--heightOffset))',
          top: 'var(--heightOffset)',
        }}
        {...getFloatingProps()}
      >
        <div className={headerStyle}>
          {heading}
          <div className={cx(controlStyles, closeControlStyle)}>
            <Button
              iconName={'x'}
              onClick={zoomOutAndClose}
              color='white'
              className={closeButtonStyle}
              // background must beat Button's more-specific `&:hover:not([aria-disabled='true'])`
              // atomic rule; inline style wins over all class rules in every state.
              style={{ background: 'none' }}
            />
          </div>
        </div>
        {children}
      </div>
    </FloatingPortal>
  );
};

ImagePreviewModal.displayName = 'ImagePreviewModal';
